import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Chip,
  Divider,
  Grid,
  Paper,
  IconButton,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch
} from '@mui/material';
import {
  Send as SendIcon,
  Refresh as RefreshIcon,
  Info as InfoIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  AttachFile as AttachFileIcon
} from '@mui/icons-material';
import { useForm, Controller } from 'react-hook-form';
import toast from 'react-hot-toast';
import { formsAPI } from '../../services/api';
import FileUpload from './FileUpload';

const TestForm = ({ website, onClose }) => {
  const [loading, setLoading] = useState(false);
  const [submissionResult, setSubmissionResult] = useState(null);
  const [formConfig, setFormConfig] = useState(null);
  const [configLoading, setConfigLoading] = useState(true);
  const [files, setFiles] = useState([]);
  const [includeFiles, setIncludeFiles] = useState(false);

  const { control, handleSubmit, reset, formState: { errors } } = useForm({
    defaultValues: {
      name: '',
      email: '',
      message: '',
      phone: '',
      company: '',
      subject: ''
    }
  });

  // Load form configuration
  useEffect(() => {
    const loadFormConfig = async () => {
      if (!website?.siteId) return;
      
      try {
        setConfigLoading(true);
        const response = await formsAPI.getFormConfig(website.siteId);
        setFormConfig(response.data.website);
      } catch (error) {
        console.error('Failed to load form config:', error);
        toast.error('Failed to load form configuration');
      } finally {
        setConfigLoading(false);
      }
    };

    loadFormConfig();
  }, [website?.siteId]);

  const onSubmit = async (data) => {
    if (!website?.siteId) {
      toast.error('No website selected');
      return;
    }

    try {
      setLoading(true);
      setSubmissionResult(null);

      // Filter out empty fields
      const formData = Object.entries(data)
        .filter(([key, value]) => value && value.trim() !== '')
        .reduce((acc, [key, value]) => {
          acc[key] = value.trim();
          return acc;
        }, {});

      let response;
      if (includeFiles && files.length > 0) {
        response = await formsAPI.submitFormWithFiles(website.siteId, formData, files);
      } else {
        response = await formsAPI.submitForm(website.siteId, formData);
      }
      
      setSubmissionResult({
        success: true,
        data: response.data,
        submittedData: formData
      });
      
      toast.success('Form submitted successfully!');
      
    } catch (error) {
      console.error('Form submission error:', error);
      
      setSubmissionResult({
        success: false,
        error: error.response?.data || { message: 'Unknown error occurred' },
        submittedData: data
      });
      
      toast.error(error.response?.data?.message || 'Form submission failed');
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    reset();
    setSubmissionResult(null);
    setFiles([]);
    setIncludeFiles(false);
  };

  if (configLoading) {
    return (
      <Card>
        <CardContent>
          <Box display="flex" justifyContent="center" alignItems="center" py={4}>
            <CircularProgress />
            <Typography variant="body1" sx={{ ml: 2 }}>
              Loading form configuration...
            </Typography>
          </Box>
        </CardContent>
      </Card>
    );
  }

  if (!formConfig) {
    return (
      <Card>
        <CardContent>
          <Alert severity="error">
            Failed to load form configuration. Please check if the website exists and forms are enabled.
          </Alert>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardContent>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h6" component="h2">
            Test Form: {formConfig.name}
          </Typography>
          <Box>
            <Tooltip title="Reset form">
              <IconButton onClick={handleReset} size="small">
                <RefreshIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        {/* Form Info */}
        <Paper elevation={1} sx={{ p: 2, mb: 3, bgcolor: 'grey.50' }}>
          <Box display="flex" alignItems="center" mb={1}>
            <InfoIcon color="primary" sx={{ mr: 1 }} />
            <Typography variant="subtitle2">Form Information</Typography>
          </Box>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Typography variant="body2" color="text.secondary">
                <strong>Site ID:</strong> {formConfig.siteId}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="body2" color="text.secondary">
                <strong>Daily Limit:</strong> {formConfig.limits?.daily || 'N/A'}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="body2" color="text.secondary">
                <strong>Monthly Limit:</strong> {formConfig.limits?.monthly || 'N/A'}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="body2" color="text.secondary">
                <strong>Form Fields:</strong> {formConfig.formFields?.length || 0}
              </Typography>
            </Grid>
          </Grid>
        </Paper>

        <Divider sx={{ mb: 3 }} />

        {/* Test Form */}
        <form onSubmit={handleSubmit(onSubmit)}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Controller
                name="name"
                control={control}
                rules={{ 
                  required: 'Name is required',
                  minLength: { value: 2, message: 'Name must be at least 2 characters' }
                }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Full Name"
                    fullWidth
                    error={!!errors.name}
                    helperText={errors.name?.message}
                    disabled={loading}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <Controller
                name="email"
                control={control}
                rules={{ 
                  required: 'Email is required',
                  pattern: {
                    value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                    message: 'Please enter a valid email address'
                  }
                }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Email Address"
                    type="email"
                    fullWidth
                    error={!!errors.email}
                    helperText={errors.email?.message}
                    disabled={loading}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <Controller
                name="phone"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Phone Number (Optional)"
                    fullWidth
                    disabled={loading}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <Controller
                name="company"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Company (Optional)"
                    fullWidth
                    disabled={loading}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12}>
              <Controller
                name="subject"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Subject (Optional)"
                    fullWidth
                    disabled={loading}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12}>
              <Controller
                name="message"
                control={control}
                rules={{ 
                  required: 'Message is required',
                  minLength: { value: 10, message: 'Message must be at least 10 characters' }
                }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Message"
                    multiline
                    rows={4}
                    fullWidth
                    error={!!errors.message}
                    helperText={errors.message?.message}
                    disabled={loading}
                  />
                )}
              />
            </Grid>

            {/* File Upload Section */}
            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <Box mb={2}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={includeFiles}
                      onChange={(e) => setIncludeFiles(e.target.checked)}
                      disabled={loading}
                    />
                  }
                  label={
                    <Box display="flex" alignItems="center">
                      <AttachFileIcon sx={{ mr: 1 }} />
                      <Typography variant="body2">
                        Include File Attachments (Test File Upload)
                      </Typography>
                    </Box>
                  }
                />
              </Box>
              
              {includeFiles && (
                <Box>
                  <Typography variant="subtitle2" gutterBottom>
                    File Attachments
                  </Typography>
                  <FileUpload
                    files={files}
                    onFilesChange={setFiles}
                    disabled={loading}
                    maxFiles={5}
                    maxSize={10 * 1024 * 1024} // 10MB
                  />
                </Box>
              )}
            </Grid>

            <Grid item xs={12}>
              <Box display="flex" gap={2} justifyContent="flex-end">
                <Button
                  type="button"
                  variant="outlined"
                  onClick={handleReset}
                  disabled={loading}
                >
                  Reset
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  startIcon={loading ? <CircularProgress size={20} /> : <SendIcon />}
                  disabled={loading}
                >
                  {loading ? 'Submitting...' : 'Submit Test Form'}
                </Button>
              </Box>
            </Grid>
          </Grid>
        </form>

        {/* Submission Result */}
        {submissionResult && (
          <Box mt={3}>
            <Divider sx={{ mb: 2 }} />
            <Typography variant="h6" gutterBottom>
              Submission Result
            </Typography>
            
            {submissionResult.success ? (
              <Alert 
                severity="success" 
                icon={<CheckCircleIcon />}
                sx={{ mb: 2 }}
              >
                <Typography variant="subtitle2" gutterBottom>
                  Form submitted successfully!
                </Typography>
                <Typography variant="body2">
                  Submission ID: {submissionResult.data.submissionId}
                </Typography>
                <Typography variant="body2">
                  Timestamp: {new Date(submissionResult.data.timestamp).toLocaleString()}
                </Typography>
                {submissionResult.data.filesUploaded > 0 && (
                  <Typography variant="body2">
                    Files Uploaded: {submissionResult.data.filesUploaded}
                  </Typography>
                )}
              </Alert>
            ) : (
              <Alert 
                severity="error" 
                icon={<ErrorIcon />}
                sx={{ mb: 2 }}
              >
                <Typography variant="subtitle2" gutterBottom>
                  Form submission failed
                </Typography>
                <Typography variant="body2">
                  {submissionResult.error.message}
                </Typography>
                {submissionResult.error.errors && (
                  <Box mt={1}>
                    {submissionResult.error.errors.map((error, index) => (
                      <Chip
                        key={index}
                        label={`${error.field}: ${error.message}`}
                        color="error"
                        variant="outlined"
                        size="small"
                        sx={{ mr: 1, mb: 1 }}
                      />
                    ))}
                  </Box>
                )}
              </Alert>
            )}

            {/* Submitted Data Preview */}
            <Paper elevation={1} sx={{ p: 2, bgcolor: 'grey.50' }}>
              <Typography variant="subtitle2" gutterBottom>
                Submitted Data:
              </Typography>
              <Box component="pre" sx={{ 
                fontSize: '0.875rem', 
                overflow: 'auto',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word'
              }}>
                {JSON.stringify(submissionResult.submittedData, null, 2)}
              </Box>
            </Paper>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default TestForm; 